import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AdminNotificationContext = createContext(null);

export const useAdminNotifications = () => {
  const ctx = useContext(AdminNotificationContext);
  if (!ctx) throw new Error('useAdminNotifications must be used within AdminNotificationProvider');
  return ctx;
};

let toastIdCounter = 0;
let notifIdCounter = 0;

export const AdminNotificationProvider = ({ children }) => {
  // Toast queue
  const [toasts, setToasts] = useState([]);
  // Bell notification history (last 10 critical events)
  const [bellNotifications, setBellNotifications] = useState([]);
  // Sidebar badge counts
  const [badges, setBadges] = useState({
    moderation: 0,
    users: 0,
    rewards: 0,
    audit: 0,
  });

  const channelsRef = useRef([]);

  // ── Toast helpers ──────────────────────────────────────────────────────────
  const addToast = useCallback((toast) => {
    const id = ++toastIdCounter;
    const ts = new Date();
    setToasts((prev) => [...prev, { ...toast, id, timestamp: ts }]);
    // Auto-dismiss after 6 seconds
    setTimeout(() => {
      setToasts((prev) => prev?.filter((t) => t?.id !== id));
    }, 6000);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev?.filter((t) => t?.id !== id));
  }, []);

  // ── Bell notification helpers ──────────────────────────────────────────────
  const addBellNotification = useCallback((notif) => {
    const id = ++notifIdCounter;
    const ts = new Date();
    setBellNotifications((prev) => [
      { ...notif, id, timestamp: ts, read: false },
      ...prev,
    ]?.slice(0, 10));
  }, []);

  const markAllRead = useCallback(() => {
    setBellNotifications((prev) => prev?.map((n) => ({ ...n, read: true })));
  }, []);

  const unreadCount = bellNotifications?.filter((n) => !n?.read)?.length;

  // ── Badge count loaders ────────────────────────────────────────────────────
  const loadBadgeCounts = useCallback(async () => {
    try {
      const [modRes, usersRes, rewardsRes, auditRes] = await Promise.all([
        supabase?.from('transactions')?.select('*', { count: 'exact', head: true })?.eq('risk_flag', true)?.eq('status', 'pending'),
        supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true })?.eq('is_active', false),
        supabase?.from('wallet_ledger')?.select('*', { count: 'exact', head: true })?.eq('ledger_type', 'reversal'),
        supabase?.from('audit_logs')?.select('*', { count: 'exact', head: true })?.eq('severity', 'critical')?.eq('reviewed', false),
      ]);
      setBadges({
        moderation: modRes?.count || 0,
        users: usersRes?.count || 0,
        rewards: rewardsRes?.count || 0,
        audit: auditRes?.count || 0,
      });
    } catch (err) {
      console.error('[AdminNotifications] loadBadgeCounts error:', err);
    }
  }, []);

  // ── Realtime subscriptions ─────────────────────────────────────────────────
  useEffect(() => {
    if (!supabase?.channel) return;

    // 1. High-risk submissions (transactions INSERT with risk_flag=true)
    const txChannel = supabase?.channel('admin-notif-transactions')?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'transactions' }, (payload) => {
        if (payload?.new?.risk_flag === true) {
          addToast({
            type: 'critical',
            icon: 'AlertTriangle',
            title: 'High-Risk Submission Flagged',
            message: `A new submission has been flagged as high-risk and requires immediate review.`,
          });
          addBellNotification({
            type: 'critical',
            icon: 'AlertTriangle',
            title: 'High-Risk Submission Flagged',
            message: 'New high-risk submission requires review.',
            section: 'moderation',
          });
          setBadges((prev) => ({ ...prev, moderation: prev?.moderation + 1 }));
        }
      })?.subscribe();

    // 2. Account suspensions (user_profiles UPDATE where is_active=false)
    const usersChannel = supabase?.channel('admin-notif-users')?.on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'user_profiles' }, (payload) => {
        if (payload?.new?.is_active === false && payload?.old?.is_active !== false) {
          const name = payload?.new?.full_name || payload?.new?.email || 'A user';
          addToast({
            type: 'warning',
            icon: 'UserX',
            title: 'Account Suspended',
            message: `${name}'s account has been suspended.`,
          });
          addBellNotification({
            type: 'warning',
            icon: 'UserX',
            title: 'Account Suspended',
            message: `${name}'s account was suspended.`,
            section: 'users',
          });
          setBadges((prev) => ({ ...prev, users: prev?.users + 1 }));
        }
      })?.subscribe();

    // 3. Reward reversals (wallet_ledger INSERT where ledger_type=reversal)
    const ledgerChannel = supabase?.channel('admin-notif-ledger')?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'wallet_ledger' }, (payload) => {
        if (payload?.new?.ledger_type === 'reversal') {
          addToast({
            type: 'warning-yellow',
            icon: 'RefreshCw',
            title: 'Reward Reversal Processed',
            message: `A reward reversal of $${parseFloat(payload?.new?.amount || 0)?.toFixed(2)} has been processed.`,
          });
          addBellNotification({
            type: 'warning-yellow',
            icon: 'RefreshCw',
            title: 'Reward Reversal Processed',
            message: `Reversal of $${parseFloat(payload?.new?.amount || 0)?.toFixed(2)} processed.`,
            section: 'rewards',
          });
          setBadges((prev) => ({ ...prev, rewards: prev?.rewards + 1 }));
        }
      })?.subscribe();

    // 4. System anomalies (audit_logs INSERT where severity=critical)
    const auditChannel = supabase?.channel('admin-notif-audit')?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'audit_logs' }, (payload) => {
        if (payload?.new?.severity === 'critical') {
          addToast({
            type: 'critical',
            icon: 'ShieldAlert',
            title: 'System Anomaly Detected',
            message: payload?.new?.description || 'A critical system anomaly has been logged.',
          });
          addBellNotification({
            type: 'critical',
            icon: 'ShieldAlert',
            title: 'System Anomaly Detected',
            message: payload?.new?.description || 'Critical anomaly logged.',
            section: 'audit',
          });
          setBadges((prev) => ({ ...prev, audit: prev?.audit + 1 }));
        }
      })?.subscribe();

    channelsRef.current = [txChannel, usersChannel, ledgerChannel, auditChannel];

    return () => {
      channelsRef?.current?.forEach((ch) => supabase?.removeChannel(ch));
      channelsRef.current = [];
    };
  }, [addToast, addBellNotification]);

  // Load initial badge counts on mount
  useEffect(() => {
    loadBadgeCounts();
  }, [loadBadgeCounts]);

  return (
    <AdminNotificationContext.Provider
      value={{
        toasts,
        dismissToast,
        badges,
        bellNotifications,
        unreadCount,
        markAllRead,
        addToast,
      }}
    >
      {children}
    </AdminNotificationContext.Provider>
  );
};

export default AdminNotificationProvider;
